import React, { useEffect, useState } from 'react';
import { useMutation, useQuery } from 'react-query';

import { ColumnsType, TableProps } from 'antd/es/table';
import classNames from 'classnames';
import dayjs from 'dayjs';
import styled from 'styled-components';

import { getCommunityAudits, runCommunityAudit } from 'apis/CommunityAPI';
import ConfirmModal from 'common/Modal/ConfirmModal';
import { StyledTable } from 'common/Table/StyledTable';
import { useRootContext } from 'components/layout/RootContext';
import Button from 'components/lib/Button';
import Loader from 'components/lib/Loader';
import {
  SimpleListModal,
  SimpleListModalContent,
  SimpleListModalDescription,
  SimpleListModalTitleStrong,
} from 'components/lib/SimpleListModal';
import { SimpleListModalStickyFooter } from 'components/lib/SimpleListModalComponents';
import {
  COMMUNITY_AUDIT_STATUS,
  COMMUNITY_AUDIT_STATUS_LABEL,
  COMMUNITY_AUDIT_TYPE,
  COMMUNITY_AUDIT_TYPE_LABEL,
} from 'constants/audits';
import { CommunityAdminListType, CommunityAuditType } from 'types/communities';

import CommunityAuditsFilters from './CommunityAuditsFilters';

type Props = {
  openModal: boolean;
  communityInfo: CommunityAdminListType | undefined;
  onClose: () => void;
};

type CommunityAuditsTableProps = {
  audits: CommunityAuditType[];
};

type AuditProposition = {
  normalized_url?: string;
  url?: string;
};

type AuditDetails = Record<string, any> & {
  candidate_urls?: string[];
  propositions?: AuditProposition[];
};

const AUDITS_TABLE_SCROLL_HEIGHT = 360;
const AUDIT_DETAILS_MAX_HEIGHT = 220;

const StyledModal = styled(SimpleListModal)`
  .ant-modal-content {
    width: 960px;
  }

  .ant-modal-body {
    height: 620px;
  }
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  min-width: 72px;
  justify-content: center;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-family: var(--font-bold);
  color: var(--text-primary);
  background: var(--faded-blue);

  &.resolved {
    color: var(--positive-green);
    background: var(--positive-green-light);
  }
`;

const Details = styled.pre`
  margin: 0;
  padding: 12px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  max-height: ${AUDIT_DETAILS_MAX_HEIGHT}px;
  overflow: auto;
  scrollbar-gutter: stable;
  border: 1px solid var(--line-gray);
  border-radius: 4px;
  background: var(--background-gray);
  color: var(--text-primary);
  font-size: 12px;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`;

const ExpandedDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const CandidateUrls = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px;
  border: 1px solid var(--line-gray);
  border-radius: 4px;
  background: var(--background-gray);
`;

const CandidateUrlsTitle = styled.div`
  font-size: 12px;
  font-family: var(--font-bold);
  color: var(--text-primary);
`;

const CandidateUrlsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CandidateUrlLink = styled.a`
  color: var(--competitor-purple);
  font-size: 12px;
  overflow-wrap: anywhere;
`;

const DetailsLink = styled.button`
  border: 0;
  padding: 0;
  background: transparent;
  color: var(--competitor-purple);
  font-family: var(--font-bold);
  font-size: 12px;
  cursor: pointer;
`;

const EmptyState = styled.div`
  flex: 1;
  padding: 30px 0;
  text-align: center;
  color: var(--text-secondary);
`;

const AuditHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const AuditHeaderDescription = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const BaseAuditsTable = (props: TableProps<CommunityAuditType>) => <StyledTable {...(props as any)} />;

const AuditsTable = styled(BaseAuditsTable)`
  &&& {
    flex: 1;
    min-height: 0;
    overflow: hidden;
    table-layout: fixed;

    .ant-table {
      height: 100%;
    }

    .ant-table-container {
      height: 100%;
    }

    .ant-table-body {
      max-height: ${AUDITS_TABLE_SCROLL_HEIGHT}px !important;
      overflow-y: auto !important;
      scrollbar-gutter: stable;
      scrollbar-width: thin;
      scrollbar-color: var(--gray-dark) transparent;
    }

    .ant-table-body::-webkit-scrollbar {
      width: 6px;
      height: 6px;
    }

    .ant-table-body::-webkit-scrollbar-track {
      border-radius: 100px;
      background-color: transparent;
    }

    .ant-table-body::-webkit-scrollbar-thumb {
      height: 100px;
      border-radius: 3px;
      background-color: var(--gray-dark);
    }

    .ant-table-body::-webkit-scrollbar-button {
      display: none;
    }

    .ant-table-tbody > tr > td {
      overflow-wrap: anywhere;
    }

    .ant-table-tbody > tr.audit-row-expanded .actions-hover-reveal {
      visibility: visible;
    }
  }
`;

const AUDIT_TYPE_OPTIONS = Object.values(COMMUNITY_AUDIT_TYPE).map((auditTypeId) => ({
  label: COMMUNITY_AUDIT_TYPE_LABEL[auditTypeId],
  value: auditTypeId,
}));

const AUDIT_STATUS_OPTIONS = [
  ...Object.values(COMMUNITY_AUDIT_STATUS).map((auditStatusId) => ({
    label: COMMUNITY_AUDIT_STATUS_LABEL[auditStatusId],
    value: auditStatusId,
  })),
];

const isString = (value: unknown): value is string => typeof value === 'string';

const getAuditCandidateUrls = (details: AuditDetails | null | undefined): string[] => {
  const candidateUrlDetails = details?.candidate_urls;
  const candidateUrls = Array.isArray(candidateUrlDetails) ? candidateUrlDetails.filter(isString) : [];
  const propositions = details?.propositions;
  const propositionUrls = Array.isArray(propositions)
    ? propositions.map((proposition) => proposition.normalized_url || proposition.url).filter(isString)
    : [];

  return [...candidateUrls, ...propositionUrls].filter((url, index, urls) => urls.indexOf(url) === index);
};

const AuditExpandedDetails = ({ details }: { details: AuditDetails }) => {
  const candidateUrls = getAuditCandidateUrls(details);

  return (
    <ExpandedDetails>
      {candidateUrls.length > 0 && (
        <CandidateUrls>
          <CandidateUrlsTitle>Candidate URLs</CandidateUrlsTitle>
          <CandidateUrlsList>
            {candidateUrls.map((candidateUrl) => (
              <CandidateUrlLink key={candidateUrl} href={candidateUrl} target="_blank" rel="noreferrer">
                {candidateUrl}
              </CandidateUrlLink>
            ))}
          </CandidateUrlsList>
        </CandidateUrls>
      )}
      <Details className="mini-scroll">{JSON.stringify(details ?? {}, null, 2)}</Details>
    </ExpandedDetails>
  );
};

const CommunityAuditsTable = ({ audits }: CommunityAuditsTableProps) => {
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);

  useEffect(() => {
    setExpandedRowKeys([]);
  }, [audits]);

  const columns: ColumnsType<CommunityAuditType> = [
    {
      title: 'TYPE',
      key: 'type',
      width: 240,
      render: (_value: unknown, item: CommunityAuditType) =>
        [item.category_name, item.type_name].filter(Boolean).join(' - '),
    },
    {
      title: 'DATE',
      key: 'updated_at',
      width: 180,
      render: (_value: unknown, item: CommunityAuditType) =>
        item.updated_at ? dayjs(item.updated_at).format('MMM D, YYYY h:mm A') : '-',
    },
    {
      title: 'STATUS',
      key: 'status',
      width: 120,
      render: (_value: unknown, item: CommunityAuditType) => (
        <StatusBadge className={classNames({ resolved: item.status === COMMUNITY_AUDIT_STATUS.RESOLVED })}>
          {item.status_name}
        </StatusBadge>
      ),
    },
    {
      title: '',
      key: 'details',
      width: 78,
      render: (_value: unknown, item: CommunityAuditType) => {
        const isExpanded = expandedRowKeys.includes(item.id);

        return (
          <DetailsLink
            className="actions-hover-reveal"
            type="button"
            onClick={() => {
              setExpandedRowKeys((currentKeys) =>
                isExpanded ? currentKeys.filter((key) => key !== item.id) : [...currentKeys, item.id]
              );
            }}
          >
            {isExpanded ? 'Hide Details' : 'Show Details'}
          </DetailsLink>
        );
      },
    },
  ];

  return (
    <AuditsTable
      rowKey="id"
      columns={columns}
      dataSource={audits}
      pagination={false}
      tableLayout="fixed"
      scroll={{ y: AUDITS_TABLE_SCROLL_HEIGHT }}
      rowClassName={(item: CommunityAuditType) => (expandedRowKeys.includes(item.id) ? 'audit-row-expanded' : '')}
      expandable={{
        expandedRowRender: (item: CommunityAuditType) => <AuditExpandedDetails details={item.details ?? {}} />,
        expandIcon: () => null,
        showExpandColumn: false,
        expandedRowKeys,
        onExpandedRowsChange: (keys) => setExpandedRowKeys([...keys]),
      }}
    />
  );
};

const CommunityAuditsModal = ({ openModal, communityInfo, onClose }: Props) => {
  const { showMessage } = useRootContext();
  const communityId = communityInfo?.id;
  const [typeFilter, setTypeFilter] = useState<number[]>([]);
  const [statusFilter, setStatusFilter] = useState<number[]>([]);
  const [runAuditConfirmOpen, setRunAuditConfirmOpen] = useState(false);
  const {
    data: audits = [],
    isLoading,
    refetch: refetchAudits,
  } = useQuery<CommunityAuditType[]>({
    queryKey: ['community', 'audits', communityId, typeFilter, statusFilter],
    queryFn: () =>
      getCommunityAudits(communityId as number, {
        type: typeFilter,
        status: statusFilter,
      }),
    enabled: Boolean(openModal && communityId),
    refetchInterval: openModal ? 3000 : false,
    refetchOnWindowFocus: false,
  });
  const { mutateAsync: runAudit, isLoading: isRunningAudit } = useMutation({
    mutationFn: () => runCommunityAudit(communityId as number),
  });

  useEffect(() => {
    setTypeFilter([]);
    setStatusFilter([]);
    setRunAuditConfirmOpen(false);
  }, [communityId, openModal]);

  const handleRunAudit = async () => {
    if (!communityId) {
      return;
    }

    try {
      await runAudit();
      setRunAuditConfirmOpen(false);
      showMessage('success', 'Community audit queued.');
      refetchAudits();
    } catch (error: any) {
      showMessage('error', error?.response?.data?.detail || 'Failed to queue community audit.');
    }
  };

  return (
    <>
      <ConfirmModal
        open={runAuditConfirmOpen}
        title="Run Community Audit?"
        description={`This will queue a website audit for ${
          communityInfo?.comunity_name ?? 'this community'
        }. Existing website URLs will be validated.`}
        confirmLabel="Run Audit"
        onConfirm={handleRunAudit}
        onCancel={() => setRunAuditConfirmOpen(false)}
        loading={isRunningAudit}
      />
      <StyledModal open={openModal} onCancel={onClose} footer={null} title="Community Audits" width="fit-content">
        <SimpleListModalContent>
          <SimpleListModalDescription>
            <AuditHeader>
              <AuditHeaderDescription>
                <div>
                  Audit history for{' '}
                  <SimpleListModalTitleStrong>{communityInfo?.comunity_name}</SimpleListModalTitleStrong>.
                </div>
                <div>{communityInfo?.company?.name}</div>
              </AuditHeaderDescription>
              <Button
                type="primary"
                disabled={!communityId || isRunningAudit}
                onClick={() => setRunAuditConfirmOpen(true)}
              >
                Run Audit
              </Button>
            </AuditHeader>
          </SimpleListModalDescription>
          <Loader active={isLoading} />
          <CommunityAuditsFilters
            typeFilter={typeFilter}
            statusFilter={statusFilter}
            typeOptions={AUDIT_TYPE_OPTIONS}
            statusOptions={AUDIT_STATUS_OPTIONS}
            onTypeFilterChange={setTypeFilter}
            onStatusFilterChange={setStatusFilter}
          />
          {!isLoading && audits.length === 0 && (
            <EmptyState>
              {typeFilter.length > 0 || statusFilter.length > 0
                ? 'No audits match selected filters.'
                : 'No audits found.'}
            </EmptyState>
          )}
          {!isLoading && audits.length > 0 && <CommunityAuditsTable audits={audits} />}
          <SimpleListModalStickyFooter>
            <Button onClick={onClose}>Close</Button>
          </SimpleListModalStickyFooter>
        </SimpleListModalContent>
      </StyledModal>
    </>
  );
};

export default CommunityAuditsModal;
